import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet, apiPost } from "../../app/api";
import type {
  Location,
  LocationsPayload,
  Vendor,
  VendorItem,
  VendorPoDraftLine,
  VendorPoReceiveResult,
  VendorPoResult,
  VendorPurchaseOrder,
  VendorPurchaseOrderLine,
  VendorPurchaseOrdersPayload,
  VendorsPayload
} from "../../types";
import {
  dateDaysFromNow,
  formatNumber,
  formatQuantity,
  locationLabel,
  vendorItemLabel,
  vendorOrderClosed
} from "../../display";

export function VendorPurchasingWorkspace({ csrfToken }: { csrfToken: string }) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [orders, setOrders] = useState<VendorPurchaseOrder[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [mode, setMode] = useState<"vendor" | "item">("vendor");
  const [vendorId, setVendorId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [expectedDelivery, setExpectedDelivery] = useState(dateDaysFromNow(3));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<VendorPoDraftLine[]>([{ key: crypto.randomUUID(), itemId: "", quantity: "", unitCost: "" }]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);
  const [receivingOrder, setReceivingOrder] = useState<VendorPurchaseOrder | null>(null);
  const [receiveLines, setReceiveLines] = useState<Record<number, { received: string; rejected: string; backordered: string; reason: string }>>({});

  const warehouseLocations = useMemo(() => (
    locations.filter(location => ["COMD", "COMW"].includes(String(location.code || "").toUpperCase()))
  ), [locations]);

  const selectedVendor = useMemo(() => (
    vendors.find(vendor => String(vendor.id) === vendorId) || null
  ), [vendorId, vendors]);

  const allVendorItems = useMemo(() => (
    vendors.flatMap(vendor => (vendor.items || []).map(item => ({
      ...item,
      vendor_id: vendor.id,
      vendor_name: vendor.vendor_name
    })))
  ), [vendors]);

  const selectableItems = useMemo(() => {
    if (mode === "item") return allVendorItems;
    const associated = (selectedVendor?.items || []).map(item => ({ ...item, vendor_id: selectedVendor?.id, vendor_name: selectedVendor?.vendor_name, associated: 1 }));
    const candidates = (selectedVendor?.candidate_items || []).map(item => ({ ...item, vendor_id: selectedVendor?.id, vendor_name: selectedVendor?.vendor_name, associated: 0 }));
    return [...associated, ...candidates];
  }, [allVendorItems, mode, selectedVendor]);

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter(order => [
      order.vendor_po_number,
      order.vendor_name,
      order.destination_code,
      order.destination_name,
      order.status,
      order.accounting_status,
      order.expected_delivery,
      ...(order.lines || []).flatMap(line => [line.sku, line.product_name, line.po_item_name])
    ].some(value => String(value || "").toLowerCase().includes(term)));
  }, [orders, search]);

  const loadVendorPurchasing = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [vendorsPayload, ordersPayload, locationsPayload] = await Promise.all([
        apiGet<VendorsPayload>("/api/vendors"),
        apiGet<VendorPurchaseOrdersPayload>("/api/vendor-purchase-orders"),
        apiGet<LocationsPayload>("/api/locations")
      ]);
      const vendorRows = vendorsPayload.vendors || [];
      const locationRows = (locationsPayload.locations || []).filter(location => ["COMD", "COMW"].includes(String(location.code || "").toUpperCase()));
      setVendors(vendorRows);
      setOrders(ordersPayload.vendor_purchase_orders || []);
      setLocations(locationRows);
      setVendorId(current => current || String(vendorRows[0]?.id || ""));
      setDestinationId(current => current || String(locationRows[0]?.id || ""));
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Vendor purchasing could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadVendorPurchasing();
  }, [loadVendorPurchasing]);

  useEffect(() => {
    setLines(current => current.map(line => {
      if (line.itemId && selectableItems.some(item => String(item.id) === line.itemId)) return line;
      const first = selectableItems[0];
      return {
        ...line,
        itemId: first ? String(first.id) : "",
        quantity: first ? String(first.minimum_lot_size || 1) : line.quantity,
        unitCost: first?.unit_cost !== null && first?.unit_cost !== undefined ? String(first.unit_cost) : line.unitCost
      };
    }));
  }, [selectableItems]);

  const updateLine = useCallback((key: string, updates: Partial<VendorPoDraftLine>) => {
    setLines(current => current.map(line => {
      if (line.key !== key) return line;
      const updated = { ...line, ...updates };
      if (updates.itemId !== undefined) {
        const item = selectableItems.find(option => String(option.id) === updates.itemId);
        if (item) {
          updated.quantity = String(item.minimum_lot_size || 1);
          updated.unitCost = item.unit_cost !== null && item.unit_cost !== undefined ? String(item.unit_cost) : "";
        }
      }
      return updated;
    }));
  }, [selectableItems]);

  const addLines = useCallback((count: number) => {
    setLines(current => {
      const next = [...current];
      while (next.length < 40 && count > 0) {
        const item = selectableItems[0];
        next.push({
          key: crypto.randomUUID(),
          itemId: item ? String(item.id) : "",
          quantity: item ? String(item.minimum_lot_size || 1) : "",
          unitCost: item?.unit_cost !== null && item?.unit_cost !== undefined ? String(item.unit_cost) : ""
        });
        count -= 1;
      }
      return next;
    });
  }, [selectableItems]);

  const removeLine = useCallback((key: string) => {
    setLines(current => current.length <= 1 ? current : current.filter(line => line.key !== key));
  }, []);

  const submitVendorPo = useCallback(async (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setMessage(null);
    const draftLines = lines.map(line => {
      const item = selectableItems.find(option => String(option.id) === line.itemId);
      const quantity = Number(line.quantity || 0);
      if (!item || quantity <= 0) return null;
      const itemId = String(item.id);
      const vendorItemId = itemId.startsWith("product:") ? 0 : Number(itemId || 0);
      const row: Record<string, unknown> = {
        vendor_item_id: vendorItemId,
        product_id: Number(item.product_id || 0),
        quantity
      };
      if (line.unitCost !== "") row.unit_cost = Number(line.unitCost || 0);
      return { vendorId: Number(item.vendor_id || vendorId || 0), row };
    }).filter(Boolean) as Array<{ vendorId: number; row: Record<string, unknown> }>;
    if (!draftLines.length) {
      setMessage({ variant: "danger", title: "Add at least one item with a quantity." });
      return;
    }
    const vendorIds = [...new Set(draftLines.map(line => line.vendorId).filter(Boolean))];
    const submittedVendorId = mode === "item" ? vendorIds[0] : Number(vendorId || 0);
    if (!submittedVendorId) {
      setMessage({ variant: "danger", title: "Choose a vendor before submitting." });
      return;
    }
    if (vendorIds.length > 1) {
      setMessage({ variant: "danger", title: "One Vendor PO can only be sent to one vendor. Split different vendors into separate POs." });
      return;
    }
    if (!destinationId) {
      setMessage({ variant: "danger", title: "Choose COMD or COMW as the destination." });
      return;
    }
    setSaving(true);
    try {
      const result = await apiPost<VendorPoResult>("/api/vendor-purchase-orders", {
        vendor_id: submittedVendorId,
        destination_location_id: destinationId,
        expected_delivery: expectedDelivery,
        notes,
        lines: draftLines.map(line => line.row)
      }, csrfToken);
      setMessage({ variant: "success", title: `Submitted ${result.vendor_po_number || "Vendor PO"}.` });
      setNotes("");
      setLines([{ key: crypto.randomUUID(), itemId: selectableItems[0] ? String(selectableItems[0].id) : "", quantity: selectableItems[0] ? String(selectableItems[0].minimum_lot_size || 1) : "", unitCost: selectableItems[0]?.unit_cost !== null && selectableItems[0]?.unit_cost !== undefined ? String(selectableItems[0].unit_cost) : "" }]);
      await loadVendorPurchasing();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Vendor PO could not be submitted." });
    } finally {
      setSaving(false);
    }
  }, [csrfToken, destinationId, expectedDelivery, lines, loadVendorPurchasing, mode, notes, selectableItems, vendorId]);

  const openReceive = useCallback((order: VendorPurchaseOrder) => {
    const defaults: Record<number, { received: string; rejected: string; backordered: string; reason: string }> = {};
    for (const line of order.lines || []) {
      defaults[line.id] = {
        received: String(line.quantity || 0),
        rejected: "0",
        backordered: "0",
        reason: ""
      };
    }
    setReceiveLines(defaults);
    setReceivingOrder(order);
  }, []);

  const submitReceiving = useCallback(async () => {
    if (!receivingOrder) return;
    setSaving(true);
    setMessage(null);
    try {
      const result = await apiPost<VendorPoReceiveResult>("/api/vendor-purchase-orders/receive", {
        vendor_po_id: receivingOrder.id,
        lines: (receivingOrder.lines || []).map(line => ({
          line_id: line.id,
          received_quantity: Number(receiveLines[line.id]?.received || 0),
          rejected_quantity: Number(receiveLines[line.id]?.rejected || 0),
          backordered_quantity: Number(receiveLines[line.id]?.backordered || 0),
          reason: receiveLines[line.id]?.reason || ""
        }))
      }, csrfToken);
      setMessage({ variant: "success", title: `${receivingOrder.vendor_po_number || "Vendor PO"} received: ${result.status || "posted"}.` });
      setReceivingOrder(null);
      await loadVendorPurchasing();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Vendor PO could not be received." });
    } finally {
      setSaving(false);
    }
  }, [csrfToken, loadVendorPurchasing, receiveLines, receivingOrder]);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-vendor-purchasing-view" className="pf-toj-vendor-purchasing">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
          <h1>Vendor Purchasing</h1>
            <p>Create outside vendor orders for COMD or COMW, then receive delivered goods into inventory.</p>
          </FlexItem>
          {loading && (
            <FlexItem>
              <Spinner size="md" aria-label="Loading Vendor Purchasing" />
            </FlexItem>
          )}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <div className="pf-toj-vendor-grid">
          <Card className="pf-toj-work-card pf-toj-primary-work-card">
            <CardBody>
              <h2>Create Vendor PO</h2>
              <p className="pf-toj-muted">Enter as few as one item or show up to 40 lines for larger orders.</p>
              <Form onSubmit={event => void submitVendorPo(event)} className="pf-toj-form">
                <div className="pf-toj-field-row">
                  <FormGroup label="Order mode" fieldId="pf-vendor-po-mode">
                    <FormSelect id="pf-vendor-po-mode" value={mode} onChange={(_event, value) => setMode(String(value) === "item" ? "item" : "vendor")}>
                      <FormSelectOption value="vendor" label="Choose vendor first" />
                      <FormSelectOption value="item" label="Choose item first" />
                    </FormSelect>
                  </FormGroup>
                  <FormGroup label="Vendor" isRequired fieldId="pf-vendor-po-vendor">
                    <FormSelect id="pf-vendor-po-vendor" value={vendorId} onChange={(_event, value) => setVendorId(String(value))} isDisabled={mode === "item"}>
                      {vendors.map(vendor => <FormSelectOption key={vendor.id} value={String(vendor.id)} label={vendor.vendor_name} />)}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup label="Destination" isRequired fieldId="pf-vendor-po-destination">
                    <FormSelect id="pf-vendor-po-destination" value={destinationId} onChange={(_event, value) => setDestinationId(String(value))}>
                      {warehouseLocations.map(location => <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />)}
                    </FormSelect>
                  </FormGroup>
                </div>
                <div className="pf-toj-field-row">
                  <FormGroup label="Expected delivery" fieldId="pf-vendor-po-delivery">
                    <TextInput id="pf-vendor-po-delivery" type="date" value={expectedDelivery} onChange={(_event, value) => setExpectedDelivery(String(value))} />
                  </FormGroup>
                  <FormGroup label="Notes" fieldId="pf-vendor-po-notes">
                    <TextInput id="pf-vendor-po-notes" value={notes} onChange={(_event, value) => setNotes(String(value))} placeholder="Optional" />
                  </FormGroup>
                </div>
                <div className="pf-toj-lines-head">
                  <div>
                    <strong>Order lines</strong>
                    <span>{formatNumber(lines.length)} shown</span>
                  </div>
                  <div className="pf-toj-secondary-actions">
                    <Button variant="secondary" onClick={() => addLines(1)} isDisabled={lines.length >= 40}>Add item</Button>
                    <Button variant="secondary" onClick={() => addLines(5)} isDisabled={lines.length >= 40}>Add 5 items</Button>
                    <Button variant="secondary" onClick={() => addLines(40 - lines.length)} isDisabled={lines.length >= 40}>Show 40 lines</Button>
                  </div>
                </div>
                <div className="pf-toj-vendor-lines" aria-label="Vendor PO lines">
                  <div className="pf-toj-vendor-line-head" aria-hidden="true">
                    <span />
                    <span>Vendor item</span>
                    <span>Quantity</span>
                    <span>Cost</span>
                    <span>Lot minimum</span>
                    <span>Status</span>
                    <span />
                  </div>
                  {lines.map((line, index) => {
                    const selectedItem = selectableItems.find(item => String(item.id) === line.itemId);
                    return (
                      <div key={line.key} className="pf-toj-vendor-line">
                        <span className="pf-toj-line-number">{index + 1}</span>
                        <FormSelect aria-label={`Vendor item line ${index + 1}`} value={line.itemId} onChange={(_event, value) => updateLine(line.key, { itemId: String(value) })}>
                          {selectableItems.map(item => (
                            <FormSelectOption key={String(item.id)} value={String(item.id)} label={`${vendorItemLabel(item)}${mode === "item" ? ` | ${item.vendor_name || ""}` : ""}`} />
                          ))}
                        </FormSelect>
                        <TextInput aria-label={`Quantity line ${index + 1}`} type="number" min="0.01" step="0.01" value={line.quantity} onChange={(_event, value) => updateLine(line.key, { quantity: String(value) })} />
                        <TextInput aria-label={`Unit cost line ${index + 1}`} type="number" min="0" step="0.01" value={line.unitCost} onChange={(_event, value) => updateLine(line.key, { unitCost: String(value) })} placeholder="Cost" />
                        <span className="pf-toj-line-minimum">{formatQuantity(Number(selectedItem?.minimum_lot_size || 0))}</span>
                        <Label color={selectedItem?.associated ? "green" : "blue"}>{selectedItem?.associated ? "Vendor item" : "New vendor link"}</Label>
                        <Button variant="plain" onClick={() => removeLine(line.key)} isDisabled={lines.length <= 1}>Remove</Button>
                      </div>
                    );
                  })}
                </div>
                <Button type="submit" variant="primary" isLoading={saving} isDisabled={saving || loading || !selectableItems.length} onClick={event => void submitVendorPo(event)}>
                  Submit Vendor PO
                </Button>
              </Form>
            </CardBody>
          </Card>
          <Card className="pf-toj-work-card">
            <CardBody>
              <Flex alignItems={{ default: "alignItemsCenter" }} justifyContent={{ default: "justifyContentSpaceBetween" }}>
                <FlexItem>
                  <h2>Vendor PO Tracking</h2>
                  <p className="pf-toj-muted">Open orders, receiving status, and accounting handoff status.</p>
                </FlexItem>
                <FlexItem>
                  <Button variant="secondary" onClick={() => void loadVendorPurchasing()}>Refresh</Button>
                </FlexItem>
              </Flex>
              <TextInput aria-label="Search vendor POs" placeholder="Search vendor, PO number, status, item, or destination" value={search} onChange={(_event, value) => setSearch(String(value))} className="pf-toj-standalone-search" />
              <div className="pf-toj-table-wrap">
                <Table aria-label="Vendor purchase orders" variant="compact">
                  <Thead>
                    <Tr>
                      <Th>PO</Th>
                      <Th>Vendor</Th>
                      <Th>Destination</Th>
                      <Th>Status</Th>
                      <Th>Accounting</Th>
                      <Th>Delivery</Th>
                      <Th>Items</Th>
                      <Th>Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredOrders.map(order => (
                      <Tr key={order.id}>
                        <Td dataLabel="PO"><strong>{order.vendor_po_number || ""}</strong></Td>
                        <Td dataLabel="Vendor">{order.vendor_name || ""}</Td>
                        <Td dataLabel="Destination">{[order.destination_code, order.destination_name].filter(Boolean).join(" - ")}</Td>
                        <Td dataLabel="Status"><Label color={vendorOrderClosed(order) ? "green" : "blue"}>{order.status || "Submitted"}</Label></Td>
                        <Td dataLabel="Accounting"><Label color={order.accounting_status === "Posted" ? "green" : "grey"}>{order.accounting_status || "Not Sent"}</Label></Td>
                        <Td dataLabel="Delivery">{order.expected_delivery || ""}</Td>
                        <Td dataLabel="Items">{formatNumber((order.lines || []).length)}</Td>
                        <Td dataLabel="Action">
                          <Button variant="link" isInline isDisabled={vendorOrderClosed(order)} onClick={() => openReceive(order)}>
                            Receive
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                    {!filteredOrders.length && !loading && (
                      <Tr><Td colSpan={8}>No Vendor POs match this view.</Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
      <Modal variant={ModalVariant.large} isOpen={Boolean(receivingOrder)} onClose={() => setReceivingOrder(null)}>
        <ModalHeader title={`Receive ${receivingOrder?.vendor_po_number || "Vendor PO"}`} />
        <ModalBody>
          <p className="pf-toj-muted">Received quantities add stock to the destination warehouse. Shortages, rejections, or backorders require a reason.</p>
          <div className="pf-toj-table-wrap">
            <Table aria-label="Receive Vendor PO lines" variant="compact">
              <Thead>
                <Tr>
                  <Th>Item</Th>
                  <Th textCenter>Ordered</Th>
                  <Th>Received</Th>
                  <Th>Rejected</Th>
                  <Th>Backorder</Th>
                  <Th>Reason</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(receivingOrder?.lines || []).map((line: VendorPurchaseOrderLine) => (
                  <Tr key={line.id}>
                    <Td dataLabel="Item">{vendorItemLabel(line as VendorItem)}</Td>
                    <Td dataLabel="Ordered" textCenter>{formatQuantity(line.quantity)}</Td>
                    <Td dataLabel="Received"><TextInput aria-label={`Received quantity ${line.id}`} type="number" min="0" step="0.01" value={receiveLines[line.id]?.received || ""} onChange={(_event, value) => setReceiveLines(current => ({ ...current, [line.id]: { ...current[line.id], received: String(value) } }))} /></Td>
                    <Td dataLabel="Rejected"><TextInput aria-label={`Rejected quantity ${line.id}`} type="number" min="0" step="0.01" value={receiveLines[line.id]?.rejected || ""} onChange={(_event, value) => setReceiveLines(current => ({ ...current, [line.id]: { ...current[line.id], rejected: String(value) } }))} /></Td>
                    <Td dataLabel="Backorder"><TextInput aria-label={`Backorder quantity ${line.id}`} type="number" min="0" step="0.01" value={receiveLines[line.id]?.backordered || ""} onChange={(_event, value) => setReceiveLines(current => ({ ...current, [line.id]: { ...current[line.id], backordered: String(value) } }))} /></Td>
                    <Td dataLabel="Reason"><TextInput aria-label={`Receiving reason ${line.id}`} value={receiveLines[line.id]?.reason || ""} onChange={(_event, value) => setReceiveLines(current => ({ ...current, [line.id]: { ...current[line.id], reason: String(value) } }))} placeholder="If needed" /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" isLoading={saving} isDisabled={saving} onClick={() => void submitReceiving()}>Receive Vendor PO</Button>
          <Button variant="secondary" onClick={() => setReceivingOrder(null)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </PageSection>
  );
}
